import {CREDENTIAL_FIELDS_BY_FACTORY, GATEWAY_CATALOG, GatewayFactory} from './constants'

export type GatewayEnvironment = 'sandbox' | 'live'

export type VendorPaymentMethod = {
  id: number
  vendor_id?: number
  country_payment_gateway_id?: number
  factory_name: string
  environment: GatewayEnvironment
  is_enabled: boolean
  is_default?: boolean
  created_at?: string
  updated_at?: string
}

export type AvailableCatalogGateway = {
  id: number
  country_id: number
  factory_name: string
  purpose?: string
  payment_type?: 'wallet' | 'card' | string | null
  is_enabled: boolean
  sort_order?: number
}

export type EnvironmentState = {
  id?: number
  is_active: boolean
  is_enabled: boolean
  last_tested?: string | null
}

export type GatewayMetricsSummary = {
  total_transactions: number
  success_rate: number
  last_transaction?: string | null
  volume?: number
}

/** Aggregated card model (one card per factory). */
export type GatewayCardModel = {
  factory_name: string
  is_connected: boolean
  catalog_id?: number
  environments: {
    sandbox?: EnvironmentState
    live?: EnvironmentState
  }
  metrics?: GatewayMetricsSummary | null
  /** Primary row id for manage/delete (prefer enabled live, else any). */
  primary_id?: number
}

export type MetricsApiGateway = {
  factory_name: string
  environment?: string | null
  attempts: number
  succeeded: number
  success_rate: number
  gross_volume: number
  last_success_at?: string | null
}

export type FilterTab = 'all' | 'connected' | 'ready'

export type CredentialField = {
  key: string
  labelKey: string
  helpKey: string
  inputType?: 'text' | 'password'
}

const HELP_KEY_BY_FIELD: Record<string, string> = {
  channel: 'payment.help_channel',
  secret: 'payment.help_secret',
  merchant_key: 'payment.help_merchant_key',
  secret_key: 'payment.help_secret_key',
  username: 'payment.help_username',
  password: 'payment.help_password',
}

/** @deprecated Prefer CREDENTIAL_FIELDS_BY_FACTORY — kept for legacy modal files. */
export function getCredentialFields(factoryName: string): CredentialField[] {
  return (CREDENTIAL_FIELDS_BY_FACTORY[factoryName] || []).map((field) => ({
    ...field,
    helpKey: HELP_KEY_BY_FIELD[field.key] || 'payment.help_api_key',
  }))
}

export function buildGatewayCards(
  configured: VendorPaymentMethod[],
  available: AvailableCatalogGateway[],
  metricsGateways: MetricsApiGateway[] = []
): GatewayCardModel[] {
  const factories = new Set<string>()
  GATEWAY_CATALOG.forEach((g) => factories.add(g.factory_name))
  available.forEach((g) => factories.add(g.factory_name))
  configured.forEach((g) => factories.add(g.factory_name))

  return Array.from(factories)
    .filter((name) => GATEWAY_CATALOG.some((c) => c.factory_name === name))
    .map((factory_name) => {
      const rows = configured.filter((r) => r.factory_name === factory_name)
      const sandbox = rows.find((r) => r.environment === 'sandbox')
      const live = rows.find((r) => r.environment === 'live')
      const catalog = available.find((a) => a.factory_name === factory_name)
      const metricRows = metricsGateways.filter((m) => m.factory_name === factory_name)
      const attempts = metricRows.reduce((sum, m) => sum + (m.attempts || 0), 0)
      const succeeded = metricRows.reduce((sum, m) => sum + (m.succeeded || 0), 0)
      const volume = metricRows.reduce((sum, m) => sum + (m.gross_volume || 0), 0)
      const last = metricRows
        .map((m) => m.last_success_at)
        .filter(Boolean)
        .sort()
        .reverse()[0]

      const primary = live || sandbox || rows[0]

      return {
        factory_name,
        is_connected: rows.length > 0,
        catalog_id: catalog?.id,
        environments: {
          sandbox: sandbox
            ? {
                id: sandbox.id,
                is_active: sandbox.is_enabled,
                is_enabled: sandbox.is_enabled,
              }
            : undefined,
          live: live
            ? {
                id: live.id,
                is_active: live.is_enabled,
                is_enabled: live.is_enabled,
              }
            : undefined,
        },
        metrics:
          rows.length > 0
            ? {
                total_transactions: attempts,
                success_rate: attempts > 0 ? Math.round((succeeded / attempts) * 10000) / 100 : 0,
                last_transaction: last || null,
                volume,
              }
            : null,
        primary_id: primary?.id,
      }
    })
    .sort((a, b) => {
      const order = GATEWAY_CATALOG.map((g) => g.factory_name)
      return (
        order.indexOf(a.factory_name as GatewayFactory) -
        order.indexOf(b.factory_name as GatewayFactory)
      )
    })
}

export function formatRelativeTime(iso?: string | null, fallback = '—'): string {
  if (!iso) {
    return fallback
  }
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) {
    return fallback
  }
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diffSec < 60) return `${diffSec}s`
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`
  return `${Math.floor(diffSec / 86400)}d`
}
